/**
 * Shared helpers for the structured-JSON artifacts. A "field" is the common
 * `{ field, value, evidence_tag }` shape used by JTBD and Persona. Values may
 * be plain prose or a bullet list encoded as `"- a\n- b"`.
 */

import { parseEvidenceTag, type EvidenceTag } from './evidence';

/** In-memory model of a `{ field, value, evidence_tag }` JSON field. */
export interface StructuredField {
  field: string;
  value: string;
  tag: EvidenceTag | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

/** Parse one JSON field object into a StructuredField (null if malformed). */
export const parseField = (raw: unknown): StructuredField | null => {
  if (!isRecord(raw)) return null;
  const field = typeof raw.field === 'string' ? raw.field : '';
  const value = typeof raw.value === 'string' ? raw.value : '';
  if (!field) return null;
  return { field, value, tag: parseEvidenceTag(raw.evidence_tag as string | undefined) };
};

/** Parse an array of JSON field objects; returns [] on non-arrays. */
export const parseFields = (raw: unknown): StructuredField[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseField).filter((f): f is StructuredField => !!f);
};

/** Serialise a StructuredField back to the JSON shape. */
export const fieldToJson = (f: StructuredField): Record<string, unknown> => ({
  field: f.field,
  value: f.value,
  evidence_tag: f.tag ?? 'Assumed',
});

/**
 * Normalize a raw field value the model sometimes over-escapes: literal "\n"
 * (a backslash + n, not a newline) shows verbatim in the card, and stray escapes
 * like "\." leak a backslash before punctuation. Turn escaped whitespace into real
 * whitespace and drop a lone backslash before a non-word char so the text reads
 * cleanly. Real newlines already present are preserved (they drive list splitting).
 */
export const cleanFieldValue = (value: string): string =>
  value
    .replace(/\\r\\n|\\n|\\r/g, '\n') // literal "\n" / "\r\n" → real newline
    .replace(/\\t/g, ' ')
    .replace(/\\([^\w\s])/g, '$1') // "\." → ".", "\," → ",", etc.
    .trim();

/** Split a field value into list items (bullet-encoded) + whether it's a list. */
export const splitValue = (value: string): { items: string[]; bulleted: boolean } => {
  const cleaned = cleanFieldValue(value);
  const lines = cleaned
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const bulleted = lines.length > 0 && lines.every((l) => /^[-*•]\s+/.test(l));
  if (bulleted) {
    return { items: lines.map((l) => l.replace(/^[-*•]\s+/, '').trim()), bulleted: true };
  }
  if (lines.length > 1) return { items: lines, bulleted: false };
  return { items: cleaned ? [cleaned] : [], bulleted: false };
};

/** Markdown for a `**Label** *(Tag)*` + value block (bullets preserved). */
export const fieldToMarkdown = (f: StructuredField): string => {
  const tag = f.tag ? ` *(${f.tag})*` : '';
  const label = `**${f.field}**${tag}`;
  return f.value ? `${label}\n${f.value}` : label;
};
